import React, { FC, Suspense } from 'react';
import { ConfigProvider } from 'antd';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  BrowserRouter, Navigate, Route, Routes, useParams,
} from 'react-router-dom';
import { enableMapSet } from 'immer';

import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import ErrorHandler from '../components/error-handler/ErrorHandler';
import Loader from '../components/loader/Loader';
import Layout from '../components/layout/Layout';
import Navigation from './Navigation';

import './App.less';
import { AuthGuard, AuthProvider } from '../services/auth/auth.service';
import { Permission } from '../dtos/user.dto';
import { ErrorPage } from '../components/error-page/ErrorPage';
import { MetricsProvider } from '../services/metrics/MetricsProvider';
import { CookieProvider } from '../services/cookie/CookieProvider';
import MetricsLoader from '../services/metrics/MetricsLoader';
import { useMonitoring } from '../services/monitoring/MonitoringProvider';
import MonitoringLoader from '../services/monitoring/MonitoringLoader';
import { NotFoundPage } from '../components/error-page/NotFoundPage';
import { useGetClientConfig } from '../services/monitoring/monitoring.service';
import { ContributionDisplayFormat, EditorConfig } from '../dtos/clientConfig.dto';
import BrowserTabTitleManager from './BrowserTabTitleManager';

enableMapSet();

const Branches = React.lazy(() => import('./pages/branches/Branches'));
const Shell = React.lazy(() => import('./pages/shell/Shell'));
const History = React.lazy(() => import('./pages/history/History'));
const UserManagement = React.lazy(() => import('./pages/user-management/UserManagement'));
const ConsentManager = React.lazy(() => import('./pages/consent/ConsentManager'));
const Editor = React.lazy(() => import('./pages/editor/Editor'));
const Bucket = React.lazy(() => import('./pages/editor/bucket/Bucket'));
const Locale = React.lazy(() => import('./pages/editor/locale/Locale'));
const Question = React.lazy(() => import('./pages/editor/question/Question'));
const Options = React.lazy(() => import('./pages/editor/options/Options'));
const PriorEnquiryChannel = React.lazy(() => import('./pages/editor/prior-enquiry-channel/PriorEnquiryChannel'));
const Line = React.lazy(() => import('./pages/editor/line/Line'));
const HomeRouter = React.lazy(() => import('./HomeRouter'));

const DEFAULT_EDITOR_CONFIG: EditorConfig = {
  chainingEnabled: false,
  maxReadmeCharacters: 10000,
  graph: {
    visibleContributionCount: 1,
    contributionDisplayFormat: ContributionDisplayFormat.LONG_FORMAT,
  },
};

const EditorAssetRoutes = (editorConfig: EditorConfig, minOptionListSearchItems: number) => (
  <>
    <Route path="bucket" element={<Bucket />} />
    <Route path="bucket/:bucketName" element={<Bucket />} />
    <Route path="option" element={<Options minOptionCountToEnableSearch={minOptionListSearchItems} />} />
    <Route path="option/:optionListName" element={<Options minOptionCountToEnableSearch={minOptionListSearchItems} />} />
    <Route path="question" element={<Question />} />
    <Route path="question/:questionName" element={<Question />} />
    <Route path="locale" element={<Locale />} />
    <Route path="locale/:localeName" element={<Locale />} />
    <Route path="prior-enquiry-channel" element={<PriorEnquiryChannel />} />
    <Route path="prior-enquiry-channel/:priorEnquiryName" element={<PriorEnquiryChannel />} />
    <Route path="enquiry-line" element={<Line editorConfig={editorConfig} />} />
    <Route path="enquiry-line/:lineName" element={<Line editorConfig={editorConfig} />} />
    <Route path="enquiry-line/:lineName/question" element={<Question />} />
    <Route path="enquiry-line/:lineName/question/:questionName" element={<Question />} />
    <Route path="wrap-up-line" element={<Line isWrapUp editorConfig={editorConfig} />} />
    <Route path="wrap-up-line/:lineName" element={<Line isWrapUp editorConfig={editorConfig} />} />
    <Route path="wrap-up-line/:lineName/question" element={<Question />} />
    <Route path="wrap-up-line/:lineName/question/:questionName" element={<Question />} />
    <Route path="root-enquiry-line" element={<Line isRoot editorConfig={editorConfig} />} />
    <Route path="root-enquiry-line/:lineName" element={<Line isRoot editorConfig={editorConfig} />} />
    <Route path="root-enquiry-line/:lineName/question" element={<Question />} />
    <Route path="root-enquiry-line/:lineName/question/:questionName" element={<Question />} />
  </>
);

const LegacyEditorRedirect: FC = () => {
  const { branchName } = useParams<{ branchName: string }>();
  return <Navigate to={`/editor/branch/${branchName}`} replace />;
};

const App: FC = (): React.ReactElement => {
  const queryResult = useGetClientConfig();
  const editorConfig: EditorConfig = queryResult.data?.editor || DEFAULT_EDITOR_CONFIG;
  const { connector } = useMonitoring();
  const {
    error, logout, isAuthenticated,
  } = useAuth0();

  const MIN_OPTION_LIST_SEARCH_ITEMS = 20;

  if (error || !isAuthenticated) {
    if (isAuthenticated) logout();
    return (<ErrorPage />);
  }

  return (
    <ErrorHandler
      onError={(errorVal, info) => {
        if (connector) {
          connector.addError(errorVal, info);
        }
        return true;
      }}
      errorFallback={ErrorPage}
      notFoundPage={<NotFoundPage />}
    >

      <ConfigProvider virtual={process.env.NODE_ENV !== 'development'}>
        <MetricsProvider>
          <AuthProvider>
            <CookieProvider>
              <MonitoringLoader />
              <MetricsLoader />
              <ReactQueryDevtools />
              <BrowserRouter>
                <BrowserTabTitleManager />
                <Layout
                  className="history"
                >
                  <Navigation key="header" />
                  <Suspense
                    fallback={<Loader />}
                    key="content"
                  >
                    <Routes>
                      <Route path="/branches" element={<AuthGuard permissions={[Permission.CanUseEditor]}><Branches /></AuthGuard>} />
                      <Route path="/branches/:branchName" element={<AuthGuard permissions={[Permission.CanUseEditor]}><Branches /></AuthGuard>} />
                      <Route path="/branches/:branchName/:section" element={<AuthGuard permissions={[Permission.CanUseEditor]}><Branches /></AuthGuard>} />
                      <Route path="/history" element={<AuthGuard permissions={[Permission.CanUseEngineHistory]}><History /></AuthGuard>} />
                      <Route path="/shell" element={<AuthGuard permissions={[Permission.CanUseEngineShell]}><Shell editorConfig={editorConfig} /></AuthGuard>} />
                      <Route path="/user-management" element={<AuthGuard permissions={[Permission.CanReadUser]}><UserManagement /></AuthGuard>} />
                      <Route path="/editor/branch/:branchName" element={<AuthGuard><Editor editorConfig={editorConfig} /></AuthGuard>}>
                        {EditorAssetRoutes(editorConfig, MIN_OPTION_LIST_SEARCH_ITEMS)}
                      </Route>
                      <Route path="/editor/branch/:branchName/tag/:tagName" element={<AuthGuard><Editor editorConfig={editorConfig} /></AuthGuard>}>
                        {EditorAssetRoutes(editorConfig, MIN_OPTION_LIST_SEARCH_ITEMS)}
                      </Route>
                      {/* Deprecated routing - redirects to /editor/branch/:branchName */}
                      <Route path="/editor/:branchName" element={<LegacyEditorRedirect />} />
                      <Route path="/consent-management" element={<AuthGuard><ConsentManager /></AuthGuard>} />
                      {/* Need to wrap in AuthGuard in order to force the AuthContext to be populated which is used by the HomeRouter */}
                      <Route
                        path="*"
                        element={<AuthGuard><HomeRouter /></AuthGuard>}
                      />
                    </Routes>
                  </Suspense>
                </Layout>
              </BrowserRouter>
            </CookieProvider>
          </AuthProvider>
        </MetricsProvider>
      </ConfigProvider>

    </ErrorHandler>
  );
};

export default withAuthenticationRequired(App, {});
